import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Divider,
  Alert,
} from '@mui/material';
import {
  Build,
  DirectionsCar,
  Person,
  Business,
  CheckCircle,
} from '@mui/icons-material';

interface MaintenanceFormDialogProps {
  open: boolean;
  onClose: () => void;
  driverName: string;
  vehicleNumber: string;
  organizationId: string;
  onSubmit: (checkData: {
    engineChecked: boolean;
    powerTrainChecked: boolean;
    brakeChecked: boolean;
    steeringChecked: boolean;
    lightingChecked: boolean;
  }) => Promise<void>;
}

export const MaintenanceFormDialog: React.FC<MaintenanceFormDialogProps> = ({
  open,
  onClose,
  driverName,
  vehicleNumber,
  organizationId,
  onSubmit,
}) => {
  const [engineChecked, setEngineChecked] = useState(false);
  const [powerTrainChecked, setPowerTrainChecked] = useState(false);
  const [brakeChecked, setBrakeChecked] = useState(false);
  const [steeringChecked, setSteeringChecked] = useState(false);
  const [lightingChecked, setLightingChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleCheckAll = () => {
    setEngineChecked(true);
    setPowerTrainChecked(true);
    setBrakeChecked(true);
    setSteeringChecked(true);
    setLightingChecked(true);
  };

  const handleReset = () => {
    setEngineChecked(false);
    setPowerTrainChecked(false);
    setBrakeChecked(false);
    setSteeringChecked(false);
    setLightingChecked(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        engineChecked,
        powerTrainChecked,
        brakeChecked,
        steeringChecked,
        lightingChecked,
      });
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error submitting maintenance:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      handleReset();
      onClose();
    }
  };

  const allChecked =
    engineChecked &&
    powerTrainChecked &&
    brakeChecked &&
    steeringChecked &&
    lightingChecked;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Build sx={{ fontSize: 28, color: '#667eea' }} />
          <Typography variant="h6" fontWeight={600}>
            점검기록 작성
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* 운전자 정보 */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f3f4f6', borderRadius: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            운전자 정보
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person sx={{ fontSize: 18, color: '#667eea' }} />
              <TextField
                value={driverName}
                label="운전자명"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DirectionsCar sx={{ fontSize: 18, color: '#10b981' }} />
              <TextField
                value={vehicleNumber}
                label="차량 번호"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business sx={{ fontSize: 18, color: '#764ba2' }} />
              <TextField
                value={organizationId}
                label="소속"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* 점검 항목 */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              점검 항목 (5가지)
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CheckCircle />}
              onClick={handleCheckAll}
              disabled={allChecked}
            >
              전체 통과
            </Button>
          </Box>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={engineChecked}
                  onChange={(e) => setEngineChecked(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    엔진
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    엔진 상태 점검
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={powerTrainChecked}
                  onChange={(e) => setPowerTrainChecked(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    동력전달
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    동력전달장치 점검
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={brakeChecked}
                  onChange={(e) => setBrakeChecked(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    제동
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    브레이크 시스템 점검
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={steeringChecked}
                  onChange={(e) => setSteeringChecked(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    조향
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    핸들 및 조향장치 점검
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={lightingChecked}
                  onChange={(e) => setLightingChecked(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    등화
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    전조등, 후미등 등 점검
                  </Typography>
                </Box>
              }
            />
          </FormGroup>
        </Box>

        {allChecked && (
          <Alert severity="success" sx={{ mt: 2 }}>
            모든 점검 항목이 통과 처리됩니다.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          취소
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={<Build />}
        >
          {submitting ? '저장 중...' : '점검 완료'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
